import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api, { getStoredToken, setStoredToken } from '../api/client';
import { invalidate as invalidateReferenceCache } from '../api/referenceCache';
import {
  ACCOUNT_ROUTE,
  AVAILABILITY_ROUTE,
  CATEGORY_PAGES,
  DATABASE_ROUTE,
  EMAIL_NOTICES_ROUTE,
  HOME_ROUTE,
  JOB_QUEUE_ROUTE,
  AUTOMATION_ROUTE,
  RESUME_FILES_ROUTE,
  TEAMS_ROUTE,
  TEMPLATE_ROUTE,
  USERS_REGISTER_ROUTE,
} from '../constants';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const res = await api.get('/api/auth/me');
      setUser(res.data.user);
      return res.data.user;
    } catch {
      setStoredToken('');
      invalidateReferenceCache();
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (username, password) => {
    const res = await api.post('/api/auth/login', { username, password });
    setStoredToken(res.data.token);
    // Reference lists are scoped to the signed-in user.
    invalidateReferenceCache();
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // ignore
    }
    setStoredToken('');
    invalidateReferenceCache();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      refreshUser,
      isAuthenticated: Boolean(user),
      role: user?.role || null,
    }),
    [user, loading, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Category pages a user may open from assigned_categories (admin/bidder). */
export function assignedCategoryPages(user) {
  if (user?.role === 'superadmin') return CATEGORY_PAGES;
  const assigned = (user?.assigned_categories || [])
    .map((c) => String(c || '').trim().toLowerCase())
    .filter(Boolean);
  if (!assigned.length) return [];
  const allowed = new Set(assigned);
  return CATEGORY_PAGES.filter((c) => allowed.has(String(c.id).toLowerCase()));
}

/** @deprecated use assignedCategoryPages */
export function bidderCategoryPages(user) {
  return assignedCategoryPages(user);
}

/** Admins in a team get every team profile; others use their own profile list. */
export function hasAssignedProfiles(user) {
  if (user?.team_id) return (user.team_profile_count || 0) > 0;
  return (user?.assigned_profile_slugs || []).length > 0;
}

export function defaultHomePath(user) {
  // Every signed-in role lands on Home, which only shows what that role can open.
  return user?.role ? HOME_ROUTE.path : '/login';
}

export function hasFeatureAccess(user, feature) {
  if (!user) return false;
  // Admin matches SuperAdmin for app features; Database routes stay SuperAdmin-only.
  if (user.role === 'superadmin' || user.role === 'admin') return true;
  if (user.role === 'caller') {
    const access = user.access || {};
    return Boolean(access[feature]);
  }
  // bidder: resume categories only
  return false;
}

export function canAccessRoute(userOrRole, path) {
  // Back-compat: older callers passed role string only
  const user = typeof userOrRole === 'string' ? { role: userOrRole } : userOrRole;
  const role = user?.role;
  if (!role) return path === '/login';

  if (path === HOME_ROUTE.path || path === '/welcome') return true;

  if (role === 'bidder') {
    if (path === '/login' || path === '/welcome' || path === ACCOUNT_ROUTE.path) return true;
    if (path === RESUME_FILES_ROUTE.path) return true;
    return assignedCategoryPages(user).some((c) => c.path === path);
  }

  if (role === 'superadmin') return true;

  // Database, email notices, user and team management, Job queue and Automation stay SuperAdmin-only.
  if (
    path === JOB_QUEUE_ROUTE.path
    || path === AUTOMATION_ROUTE.path
    || path === DATABASE_ROUTE.path
    || path === EMAIL_NOTICES_ROUTE.path
    || path === USERS_REGISTER_ROUTE.path
    || path === TEAMS_ROUTE.path
  ) {
    return false;
  }

  // Every signed-in platform user can manage their own account.
  if (path === ACCOUNT_ROUTE.path) {
    return role === 'superadmin' || role === 'admin' || role === 'caller';
  }

  if (role === 'admin') {
    if (CATEGORY_PAGES.some((c) => c.path === path)) {
      return assignedCategoryPages(user).some((c) => c.path === path);
    }
    if (path === TEMPLATE_ROUTE.path) {
      return hasAssignedProfiles(user);
    }
    return true;
  }

  if (role === 'caller') {
    // Callers always manage their own unavailable time.
    if (path === AVAILABILITY_ROUTE.path) return true;
    if (path === '/meetings/calendar') {
      return hasFeatureAccess(user, 'calendar');
    }
    if (path === '/meetings') {
      return hasFeatureAccess(user, 'interviews');
    }
    if (path.startsWith('/meetings/')) {
      return hasFeatureAccess(user, 'interviews') || hasFeatureAccess(user, 'calendar');
    }
    return false;
  }

  return false;
}
